import { useState, useEffect } from 'react';
import type { UploadFile } from 'antd';
import { useNspCoreInfoList, useUpdateNspCoreInfo } from '@hooks/graphql/useNspCoreInfoGraphQL';
import type { NspCoreInfo, UpdateNspCoreInfoInput } from '@custom-types/nspCoreInfo';
import { Form, message } from 'antd';
import { useApi } from '@hooks/useApi';

const useNspCoreInfoPage = () => {
  const [form] = Form.useForm();
  const [selectedEntry, setSelectedEntry] = useState<NspCoreInfo | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saveFeedback, setSaveFeedback] = useState<{
    status: 'success' | 'error';
    message: string;
  } | null>(null);

  const { nspCoreInfoList, loading, refetch } = useNspCoreInfoList();
  const { updateNspCoreInfo, loading: updating } = useUpdateNspCoreInfo();
  const { uploadNspLogo } = useApi();

  // Select the first entry by default, and update selectedEntry when list changes
  useEffect(() => {
    if (nspCoreInfoList.length > 0) {
      if (selectedEntry) {
        // Update selectedEntry with fresh data from the list (preserves logoUrl after upload)
        const updatedEntry = nspCoreInfoList.find(
          (entry) => entry.nspCoreInfoId === selectedEntry.nspCoreInfoId
        );
        if (updatedEntry) {
          setSelectedEntry(updatedEntry);
        }
      } else {
        // Initial selection
        setSelectedEntry(nspCoreInfoList[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nspCoreInfoList]);

  // Update form when selected entry changes
  useEffect(() => {
    if (selectedEntry) {
      form.setFieldsValue({
        companyName: selectedEntry.companyName,
        address: selectedEntry.address,
        city: selectedEntry.city,
        province: selectedEntry.province,
        postalCode: selectedEntry.postalCode,
        faxNumber: selectedEntry.faxNumber,
        hstRate: selectedEntry.hstRate,
        approverEmail: selectedEntry.approverEmail,
        energyRiskManagementEmail: selectedEntry.energyRiskManagementEmail,
        contactName: selectedEntry.contactName,
        contactPhone: selectedEntry.contactPhone,
        contactFax: selectedEntry.contactFax,
        contactEmail: selectedEntry.contactEmail,
        approverName: selectedEntry.approverName,
        nspGl: selectedEntry.nspGl,
      });
    }
  }, [selectedEntry, form]);

  const handleSave = async () => {
    try {
      setSaveFeedback(null); // Clear previous feedback
      const values = await form.validateFields();

      if (!selectedEntry) {
        return;
      }

      const input: UpdateNspCoreInfoInput = {
        companyName: values.companyName,
        address: values.address,
        city: values.city,
        province: values.province,
        postalCode: values.postalCode,
        faxNumber: values.faxNumber,
        hstRate: values.hstRate,
        approverEmail: values.approverEmail,
        energyRiskManagementEmail: values.energyRiskManagementEmail,
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        contactFax: values.contactFax,
        contactEmail: values.contactEmail,
        approverName: values.approverName,
        nspGl: values.nspGl,
      };

      const result = await updateNspCoreInfo({
        variables: {
          nspCoreInfoId: selectedEntry.nspCoreInfoId,
          input,
        },
      });

      if (result.data?.updateNspCoreInfo) {
        setSaveFeedback({
          status: 'success',
          message: 'Information updated successfully',
        });
        await refetch();
      } else {
        throw new Error('Update returned no data');
      }
    } catch (error) {
      setSaveFeedback({
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to update information',
      });
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!selectedEntry) return false;

    setUploadingLogo(true);
    try {
      await uploadNspLogo(file, selectedEntry.entryType);
      message.success('Logo uploaded successfully');
      // Refetch to get updated logoUrl
      await refetch();
      setFileList([]);
      return true; // Upload successful
    } catch {
      message.error('Failed to upload logo');
      return false; // Upload failed
    } finally {
      setUploadingLogo(false);
    }
  };

  return {
    form,
    selectedEntry,
    setSelectedEntry,
    uploadingLogo,
    fileList,
    setFileList,
    loading,
    updating,
    handleSave,
    handleLogoUpload,
    saveFeedback,
  };
};

export default useNspCoreInfoPage;
